import { Component, OnInit } from '@angular/core'
import { DatePipe } from '@angular/common'
import { Observable } from 'rxjs'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faExclamationTriangle,
  faUndo
} from '@fortawesome/free-solid-svg-icons'

import { RecordNavigationComponent } from '../_shared/record-navigation/record-navigation.component'
import { AppSyncConflictService } from '../../_services/app-sync-conflict.service'
import {
  AppSyncConflictDetail,
  AppSyncConflictGroup,
  AppSyncConflictOverview,
  AppSyncConflictResolveResult,
  AppSyncConflictScope,
  AppSyncConflictType
} from '../../_models/app-sync-conflict.interface'

// Admin-Ansicht für den Notfallspeicher der Techniker-App
// (siehe bentomax/hawk#573).
//
// Bisher gab es dafür nur die Rails-Console. Wer die stündliche Warnmail
// bekommt, soll hier sehen, was gesichert wurde, und den Fall abhaken können.
//
// Ein Vorgang wird als eine Zeile gezeigt, auch wenn die App ihn zwanzigmal
// wiederholt hat — die Wiederholungen stehen als Zähler daneben. Der erste
// Mailbestand vom 14.08.2026 bestand aus zehn Einträgen für genau einen
// blockierten PATCH.
@Component({
  selector: 'app-app-sync-conflicts',
  standalone: true,
  imports: [DatePipe, FontAwesomeModule, RecordNavigationComponent],
  templateUrl: './app-sync-conflicts.component.html',
  host: { class: 'app-page' }
})
export class AppSyncConflictsComponent implements OnInit {
  faCheck = faCheck
  faChevronDown = faChevronDown
  faChevronRight = faChevronRight
  faExclamationTriangle = faExclamationTriangle
  faUndo = faUndo

  overview: AppSyncConflictOverview | null = null
  loading = true
  scope: AppSyncConflictScope = 'open'

  // Aufgeklappte Vorgänge und bereits geladene Nutzdaten, je Fall-ID.
  expandedGroups = new Set<string>()
  payloads = new Map<number, AppSyncConflictDetail>()
  payloadLoading = new Set<number>()

  // Läuft gerade eine Aktion auf diesem Vorgang? Verhindert Doppelklicks.
  busyGroups = new Set<string>()

  constructor(private appSyncConflictService: AppSyncConflictService) {
  }

  ngOnInit(): void {
    this.load()
  }

  public load(): void {
    this.loading = true
    this.appSyncConflictService.getOverview(this.scope).subscribe({
      next: overview => {
        this.overview = overview
        this.loading = false
      },
      error: () => {
        this.overview = null
        this.loading = false
      }
    })
  }

  public setScope(scope: AppSyncConflictScope): void {
    if (this.scope === scope) {
      return
    }

    this.scope = scope
    // Aufgeklappte Vorgänge und geladene Nutzdaten gehören zur alten Liste.
    this.expandedGroups.clear()
    this.payloads.clear()
    this.load()
  }

  // Stabiler Schlüssel eines Vorgangs. Bewusst aus den Merkmalen gebildet, nach
  // denen der Server gruppiert — eine Gruppen-ID gibt es nicht, weil eine
  // Gruppe kein eigener Datensatz ist.
  public groupKey(group: AppSyncConflictGroup): string {
    return [
      group.conflict_type,
      group.resource,
      group.record_id ?? '',
      group.user?.id ?? '',
      group.resolved ? 'erledigt' : 'offen'
    ].join('|')
  }

  public isExpanded(group: AppSyncConflictGroup): boolean {
    return this.expandedGroups.has(this.groupKey(group))
  }

  public toggleGroup(group: AppSyncConflictGroup): void {
    const key = this.groupKey(group)
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key)
    } else {
      this.expandedGroups.add(key)
    }
  }

  public isBusy(group: AppSyncConflictGroup): boolean {
    return this.busyGroups.has(this.groupKey(group))
  }

  // Hakt alle Wiederholungen des Vorgangs ab.
  public resolveGroup(group: AppSyncConflictGroup): void {
    this.runGroupAction(group, ids => this.appSyncConflictService.resolve(ids))
  }

  public reopenGroup(group: AppSyncConflictGroup): void {
    this.runGroupAction(group, ids => this.appSyncConflictService.reopen(ids))
  }

  private runGroupAction(
    group: AppSyncConflictGroup,
    action: (ids: number[]) => Observable<AppSyncConflictResolveResult>
  ): void {
    const key = this.groupKey(group)
    if (this.busyGroups.has(key)) {
      return
    }

    this.busyGroups.add(key)
    action(group.conflicts.map(conflict => conflict.id)).subscribe({
      next: () => {
        this.busyGroups.delete(key)
        this.expandedGroups.delete(key)
        this.load()
      },
      error: () => {
        this.busyGroups.delete(key)
      }
    })
  }

  public isPayloadLoading(conflictId: number): boolean {
    return this.payloadLoading.has(conflictId)
  }

  public payloadFor(conflictId: number): string | null {
    const detail = this.payloads.get(conflictId)

    return detail ? JSON.stringify(detail.payload, null, 2) : null
  }

  public deviceFor(conflictId: number): string | null {
    const detail = this.payloads.get(conflictId)
    if (!detail) {
      return null
    }

    return [detail.remote_ip, detail.user_agent].filter(Boolean).join(' · ') || null
  }

  // Nutzdaten erst auf Anforderung holen — in der Liste wären es komplette
  // Checklisten-Antworten.
  public togglePayload(conflictId: number): void {
    if (this.payloads.has(conflictId)) {
      this.payloads.delete(conflictId)
      return
    }

    if (this.payloadLoading.has(conflictId)) {
      return
    }

    this.payloadLoading.add(conflictId)
    this.appSyncConflictService.getDetail(conflictId).subscribe({
      next: detail => {
        this.payloads.set(conflictId, detail)
        this.payloadLoading.delete(conflictId)
      },
      error: () => {
        this.payloadLoading.delete(conflictId)
      }
    })
  }

  public typeLabel(type: AppSyncConflictType): string {
    switch (type) {
      case 'update_target_missing':
        return 'Ziel nicht mehr vorhanden'
      case 'delete_restricted':
        return 'Löschen verweigert'
      case 'validation_failed':
        return 'Validierung fehlgeschlagen'
      case 'forbidden':
        return 'Keine Berechtigung (Lager-Modus)'
      default:
        return type
    }
  }
}
